import React, { useState, useEffect } from 'react';
import { firestore } from '../../firebase/config';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import EditModal from '../Gestor/EditModal';
import * as XLSX from 'xlsx';

// Função Helper para normalizar texto (apenas remove espaços extras e caixa alta)
const normalizarTexto = (texto) => {
    if (!texto) return '';
    return texto.trim().toUpperCase();
};

function HistoricoVisitasSupervisor({ userLocalidades, user }) {
    const [visitas, setVisitas] = useState([]);
    const [filteredVisitas, setFilteredVisitas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [agentsMap, setAgentsMap] = useState({});

    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Função para calcular a data limite (máximo 7 dias de intervalo)
    const getMaxDate = (start) => {
        if (!start) return '';
        const d = new Date(start);
        d.setDate(d.getDate() + 6); // 7 dias de intervalo (inclusive)
        return d.toISOString().split('T')[0];
    };

    // Handler para mudança na Data Inicial
    const handleStartDateChange = (e) => {
        const newStart = e.target.value;
        setStartDate(newStart);

        // Se a data final atual for menor que a nova data inicial ou exceder o limite de 7 dias, ajusta automaticamente
        if (newStart && endDate) {
            const maxDate = getMaxDate(newStart);
            if (endDate < newStart) {
                setEndDate(newStart);
            } else if (endDate > maxDate) {
                setEndDate(maxDate);
            }
        }
    };

    const [filterAgente, setFilterAgente] = useState('');
    const [filterQuarteirao, setFilterQuarteirao] = useState('');
    const [filterLocalidade, setFilterLocalidade] = useState('');
    const [showCanceladas, setShowCanceladas] = useState(false); // Toggle para mostrar canceladas


    // Buscar agentes
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const q = query(collection(firestore, "usuarios"), where("perfil", "==", "agente"));
                const querySnapshot = await getDocs(q);
                const agents = {};

                querySnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.email) {
                        agents[data.email.toLowerCase()] = data.nome || data.email;
                    }
                    agents[doc.id] = data.nome || data.email;
                });

                setAgentsMap(agents);
            } catch (error) {
                console.error("Erro ao buscar agentes: ", error);
            }
        };
        fetchAgents();
    }, []);

    // Buscar visitas COM FILTRO DE LOCALIDADES
    const handleSearch = async () => {
        if (!startDate || !endDate) {
            alert("Por favor, selecione a data de início e de fim.");
            return;
        }

        if (!userLocalidades || userLocalidades.length === 0) {
            alert("Você não tem localidades atribuídas.");
            return;
        }

        if (!filterLocalidade) {
            alert("É obrigatório selecionar uma localidade para buscar.");
            return;
        }

        setLoading(true);

        const [sYear, sMonth, sDay] = startDate.split('-');
        const start = new Date(sYear, sMonth - 1, sDay);
        start.setHours(0, 0, 0, 0);

        const [eYear, eMonth, eDay] = endDate.split('-');
        const end = new Date(eYear, eMonth - 1, eDay);
        end.setHours(23, 59, 59, 999);

        // Validação de intervalo de 7 dias
        // diffDays usando Math.ceil com end=23:59:59 e start=00:00:00 resulta em 7 para uma semana inteira.
        // Por isso o limite correto é > 7 para permitir exatamente 7 dias (dom-sáb).
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 7) { // Permite até 7 dias (domingo a sábado)
            alert("O período máximo de busca é de 7 dias para evitar sobrecarga no sistema.");
            return;
        }

        // QUERY OTIMIZADA: Filtrando por Localidade DIRETO NO SERVIDOR
        // Isso garante que traremos todas as visitas daquela localidade no período, sem limite arbitrário de 100.
        // Necessário índice composto no Firebase: localidade (Asc) + dataAtividade (Desc)

        let q;
        const localidadeNormalizada = normalizarTexto(filterLocalidade);

        q = query(
            collection(firestore, 'relatoriosDiarios_FAD1'),
            where('dataAtividade', '>=', start),
            where('dataAtividade', '<=', end),
            where('localidade', '==', localidadeNormalizada), // Filtro Principal
            orderBy('dataAtividade', 'desc')
        );

        console.log("Buscando histórico com:", { start, end, localidade: localidadeNormalizada });

        try {
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            console.log("✅ Resultados encontrados no servidor:", querySnapshot.size);
            console.log("📊 Primeiros 3 resultados:", data.slice(0, 3).map(v => ({
                id: v.id,
                idVisita: v.idVisita,
                agente: v.emailAgente || v.idAgente,
                localidade: v.localidade,
                quarteirao: v.quarteirao,
                data: v.dataAtividade?.toDate?.().toLocaleDateString('pt-BR')
            })));

            setVisitas(data);
        } catch (error) {
            console.error("Erro ao buscar histórico: ", error);
            // Fallback amigável caso falte índice
            if (error.message.includes("index")) {
                alert("Erro de Índice no Banco de Dados. Por favor, comunique o suporte técnico ou aguarde a criação automática.");
            } else {
                alert("Erro ao buscar histórico de visitas.");
            }
        }

        setLoading(false);
    };

    // Aplicar filtros adicionais
    useEffect(() => {
        let result = visitas;

        if (filterAgente) {
            result = result.filter(v => {
                const agenteKey = v.idAgente || v.emailAgente || '';
                const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                // Usa normalizarTexto para busca insensível a acentos
                return normalizarTexto(nomeAgente).includes(normalizarTexto(filterAgente));
            });
        }

        if (filterQuarteirao) {
            result = result.filter(v => v.quarteirao && v.quarteirao.toString().includes(filterQuarteirao));
        }

        if (filterLocalidade) {
            result = result.filter(v => v.localidade && normalizarTexto(v.localidade).includes(normalizarTexto(filterLocalidade)));
        }

        // Filtro de Status de Registro (Canceladas)
        if (!showCanceladas) {
            // Por padrão, exclui visitas canceladas e duplicadas (Com robustez)
            result = result.filter(v => {
                const s = v.statusRegistro ? v.statusRegistro.trim().toLowerCase() : 'ativo';
                return s === 'ativo';
            });
        }


        setFilteredVisitas(result);
    }, [visitas, filterAgente, filterQuarteirao, filterLocalidade, agentsMap, showCanceladas]);


    const handleOpenModal = (visitaDaLinha) => {
        setSelectedVisit(visitaDaLinha);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedVisit(null);
    };

    const handleSaveSuccess = () => {
        handleCloseModal();
        handleSearch(); // Recarrega os dados
    };

    // --- Exportação Geral (Igual ao Gestor) ---
    // --- Exportação Geral (FULL - Sem Limite de 100) ---
    const handleExportCSV = async () => {
        if (!startDate || !endDate) {
            alert("Selecione o intervalo de datas.");
            return;
        }
        if (!filterLocalidade) {
            alert("Selecione uma localidade para exportar.");
            return;
        }

        setLoading(true);
        try {
            const [sYear, sMonth, sDay] = startDate.split('-');
            const start = new Date(sYear, sMonth - 1, sDay);
            start.setHours(0, 0, 0, 0);

            const [eYear, eMonth, eDay] = endDate.split('-');
            const end = new Date(eYear, eMonth - 1, eDay);
            end.setHours(23, 59, 59, 999);

            // Query Completa (SEM LIMIT) para exportação
            // Nota: Mantemos o filtro de data e ordenação, mas removemos o limit(100)
            const q = query(
                collection(firestore, 'relatoriosDiarios_FAD1'),
                where('dataAtividade', '>=', start),
                where('dataAtividade', '<=', end),
                orderBy('dataAtividade', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const rawData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Aplica os mesmos filtros do cliente (Localidade, Agente, Quarteirão)
            const dataToExport = rawData.filter(v => {
                // 1. Filtro Localidade (Obrigatório e já verificado)
                if (!v.localidade || normalizarTexto(v.localidade) !== normalizarTexto(filterLocalidade)) return false;

                // 2. Filtro Agente (Opcional)
                if (filterAgente) {
                    const agenteKey = v.idAgente || v.emailAgente || '';
                    const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                    if (!normalizarTexto(nomeAgente).includes(normalizarTexto(filterAgente))) return false;
                }

                // 3. Filtro Quarteirão (Opcional)
                if (filterQuarteirao) {
                    if (!v.quarteirao || !v.quarteirao.toString().includes(filterQuarteirao)) return false;
                }

                return true;
            });

            if (dataToExport.length === 0) {
                alert("Nenhum dado encontrado para exportação com os filtros atuais.");
                setLoading(false);
                return;
            }

            exportarExcel(dataToExport, `Historico_Completo_${startDate}_${endDate}`);

        } catch (error) {
            console.error("Erro ao exportar:", error);
            alert("Erro ao gerar exportação.");
        }
        setLoading(false);
    };

    // Função Auxiliar de Geração do Excel (Separada para clareza)
    const exportarExcel = (dados, fileName) => {
        let maxAmostras = 0;
        let maxTratFocal = 0;
        let maxTratPerifocal = 0;
        dados.forEach(v => {
            if (v.amostras && v.amostras.length > maxAmostras) maxAmostras = v.amostras.length;
            if (v.tratamentosFocais && v.tratamentosFocais.length > maxTratFocal) maxTratFocal = v.tratamentosFocais.length;
            if (v.tratamentosPerifocais && v.tratamentosPerifocais.length > maxTratPerifocal) maxTratPerifocal = v.tratamentosPerifocais.length;
        });

        const dadosFormatados = dados.map(v => {
            const agenteKey = v.idAgente || v.emailAgente || '';
            const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;

            let animaisStr = 'NÃO';
            if (v.temAnimais?.toUpperCase() === 'SIM') {
                animaisStr = v.animais?.map(a => `${a.tipo}(${a.sexo}):${a.quantidade}`).join('; ') || 'NÃO ESPECIFICADO';
            }

            const atividadeMap = { '1': 'RA', '2': 'LI+T', '3': 'PE', '4': 'T', '5': 'DF', '6': 'PVE', '7': 'ID', '8': 'BT' };
            let atividadeDetalhada = atividadeMap[v.atividade] || v.atividade;
            if (v.atividade === '2' && v.subAtividadeLIT) {
                const { li, t } = v.subAtividadeLIT;
                if (li && t) atividadeDetalhada = 'LI+T (Ambos)';
                else if (li) atividadeDetalhada = 'LI+T (Apenas LI)';
                else if (t) atividadeDetalhada = 'LI+T (Apenas T)';
            }

            const visitaRow = {
                'Data Visita': v.dataAtividade?.toDate ? v.dataAtividade.toDate().toLocaleString('pt-BR') : 'Data Inválida',
                'Agente': nomeAgente,
                'Município': v.municipio,
                'Localidade': v.localidade,
                'Ciclo/Ano': v.cicloAno,
                'Zona': v.zona === '1' ? 'Urbana' : 'Rural',
                'Atividade': atividadeDetalhada,
                'Quarteirão': v.quarteirao,
                'Sequência': v.sequencia,
                'Logradouro': v.logradouro,
                'Nº Imóvel': v.numeroImovel,
                'Complemento': v.complemento,
                'Hora Entrada': v.horaEntrada,
                'Status Visita': v.visitaStatus,
                'Tipo Imóvel': v.tipoImovel,
                'Depósitos A1': v.depositos?.A1,
                'Depósitos A2': v.depositos?.A2,
                'Depósitos B': v.depositos?.B,
                'Depósitos C': v.depositos?.C,
                'Depósitos D1': v.depositos?.D1,
                'Depósitos D2': v.depositos?.D2,
                'Depósitos E': v.depositos?.E,
                'Depósitos Eliminados': v.depositoEliminado,
                'Coleta Amostra': v.coletaAmostra ? 'Sim' : 'Não',
                'Possui Animais': v.temAnimais?.toUpperCase() === 'SIM' ? 'Sim' : 'Não',
                'Detalhes dos Animais': animaisStr,
                'Latitude': v.coordenadas?.latitude,
                'Longitude': v.coordenadas?.longitude,
                'Tratamento Focal': v.tratFocalImovelTratado ? 'Sim' : 'Não',
            };

            for (let i = 0; i < maxTratFocal; i++) {
                const trat = v.tratamentosFocais?.[i];
                visitaRow[`Trat. Focal ${i + 1} - Depósito`] = trat?.depositoTratado || '';
                visitaRow[`Trat. Focal ${i + 1} - Tipo`] = trat?.tipoLarvicida || '';
                visitaRow[`Trat. Focal ${i + 1} - Gramas`] = trat?.qtdeGramas || 0;
            }

            visitaRow['Tratamento Perifocal'] = v.tratPerifocalImovelTratado ? 'Sim' : 'Não';
            for (let i = 0; i < maxTratPerifocal; i++) {
                const trat = v.tratamentosPerifocais?.[i];
                visitaRow[`Trat. Perifocal ${i + 1} - Tipo`] = trat?.tipoAdulticida || '';
                visitaRow[`Trat. Perifocal ${i + 1} - Gramas`] = trat?.qtdeGramas || 0;
            }

            if (v.coletaAmostra) {
                for (let i = 0; i < maxAmostras; i++) {
                    const amostra = v.amostras?.[i];
                    const analise = amostra?.analise;
                    visitaRow[`Amostra ${i + 1} - Depósito`] = amostra?.depositoColeta || '';
                    visitaRow[`Amostra ${i + 1} - Observação`] = amostra?.notaInicial || '';
                    visitaRow[`Amostra ${i + 1} - Status Análise`] = analise?.status || 'pendente';
                    visitaRow[`Amostra ${i + 1} - Aegypti (L)`] = analise?.aegypti?.larvas || 0;
                    visitaRow[`Amostra ${i + 1} - Aegypti (P)`] = analise?.aegypti?.pupas || 0;
                    visitaRow[`Amostra ${i + 1} - Aegypti (E)`] = analise?.aegypti?.exuvia || 0;
                    visitaRow[`Amostra ${i + 1} - Aegypti (A)`] = analise?.aegypti?.adultos || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (L)`] = analise?.albopictus?.larvas || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (P)`] = analise?.albopictus?.pupas || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (E)`] = analise?.albopictus?.exuvia || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (A)`] = analise?.albopictus?.adultos || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (L)`] = analise?.outros?.larvas || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (P)`] = analise?.outros?.pupas || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (E)`] = analise?.outros?.exuvia || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (A)`] = analise?.outros?.adultos || 0;
                }
            }

            return visitaRow;
        });

        const ws = XLSX.utils.json_to_sheet(dadosFormatados);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Histórico Geral");
        XLSX.writeFile(wb, `Historico_Visitas_${startDate}_${endDate}.xlsx`);
    };

    // --- Exportação de Pendências (Fechados não recuperados) ---
    // --- Exportação de Pendências (FULL - Sem Limite) ---
    const handleExportFechados = async () => {
        if (!startDate || !endDate) {
            alert("Selecione o intervalo de datas.");
            return;
        }
        if (!filterLocalidade) {
            alert("Selecione uma localidade para exportar pendências.");
            return;
        }

        setLoading(true);

        try {
            const [sYear, sMonth, sDay] = startDate.split('-');
            const start = new Date(sYear, sMonth - 1, sDay);
            start.setHours(0, 0, 0, 0);

            const [eYear, eMonth, eDay] = endDate.split('-');
            const end = new Date(eYear, eMonth - 1, eDay);
            end.setHours(23, 59, 59, 999);

            // Fetch Completo
            const q = query(
                collection(firestore, 'relatoriosDiarios_FAD1'),
                where('dataAtividade', '>=', start),
                where('dataAtividade', '<=', end),
                orderBy('dataAtividade', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const rawData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtra por localidade e outros
            const filteredData = rawData.filter(v => {
                if (!v.localidade || normalizarTexto(v.localidade) !== normalizarTexto(filterLocalidade)) return false;
                // (Outros filtros opcionais se desejar manter consistencia)
                return true;
            });

            // 1. Agrupar por residência
            const residencias = {};
            filteredData.forEach(v => {
                const chave = `${normalizarTexto(v.localidade)}-${normalizarTexto(v.logradouro)}-${v.numeroImovel}`;
                if (!residencias[chave]) residencias[chave] = [];
                residencias[chave].push(v);
            });

            const pendencias = [];

            // 2. Filtrar residências
            Object.values(residencias).forEach(grupo => {
                const foiRecuperadoOuInspecionado = grupo.some(v => {
                    const vs = normalizarTexto(v.visitaStatus);
                    return vs === 'RECUPERADO' || vs === 'INSPECIONADO' || vs === 'NORMAL';
                });

                if (!foiRecuperadoOuInspecionado) {
                    const visitasFechadas = grupo.filter(v => normalizarTexto(v.visitaStatus) === 'FECHADO');
                    visitasFechadas.forEach(v => {
                        const agenteKey = v.idAgente || v.emailAgente || '';
                        const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                        pendencias.push({
                            "Data": v.dataAtividade?.toDate?.().toLocaleDateString('pt-BR') || '',
                            "Agente": nomeAgente,
                            "Localidade": v.localidade || '',
                            "Quarteirão": v.quarteirao || '',
                            "Logradouro": v.logradouro || '',
                            "Nº Imóvel": v.numeroImovel || '',
                            "Status Visita": v.visitaStatus || ''
                        });
                    });
                }
            });

            if (pendencias.length === 0) {
                alert("Nenhuma pendência encontrada neste período (com filtros aplicados).");
                setLoading(false);
                return;
            }

            const ws = XLSX.utils.json_to_sheet(pendencias);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Pendências Fechados");
            XLSX.writeFile(wb, `Pendencias_Fechados_${startDate}_${endDate}.xlsx`);

        } catch (error) {
            console.error("Erro ao exportar pendências:", error);
            alert("Erro ao exportar pendências.");
        }
        setLoading(false);
    };

    if (!userLocalidades || userLocalidades.length === 0) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <h3>⚠️ Nenhuma localidade atribuída</h3>
                <p style={{ color: '#666' }}>
                    Entre em contato com o gestor para atribuir localidades ao seu perfil.
                </p>
            </div>
        );
    }

    return (
        <>
            <h2>Histórico de Visitas - Suas Localidades</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
                Localidades: <strong>{userLocalidades.join(', ')}</strong>
            </p>

            <div className="filters-container">
                <div className="input-group">
                    <label>Data de Início</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                    />
                </div>
                <div className="input-group">
                    <label>Data de Fim (Máx. 7 dias)</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        min={startDate}
                        max={getMaxDate(startDate)}
                    />
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <button onClick={handleSearch} disabled={loading} style={{ height: '40px' }}>
                        {loading ? 'Buscando...' : 'Buscar'}
                    </button>
                    <button onClick={handleExportCSV} disabled={loading || filteredVisitas.length === 0} className="export-button" style={{ height: '40px', backgroundColor: '#28a745', color: 'white' }}>
                        Exportar Geral
                    </button>
                    <button onClick={handleExportFechados} disabled={loading || filteredVisitas.length === 0} className="export-button" style={{ height: '40px', backgroundColor: '#dc3545', color: 'white' }}>
                        Exportar Pendências
                    </button>
                </div>
            </div>

            <div className="filters-container">
                <input
                    type="text"
                    placeholder="Filtrar por nome do agente..."
                    value={filterAgente}
                    onChange={(e) => setFilterAgente(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Filtrar por quarteirão..."
                    value={filterQuarteirao}
                    onChange={(e) => setFilterQuarteirao(e.target.value)}
                />
                <select
                    value={filterLocalidade}
                    onChange={(e) => setFilterLocalidade(e.target.value)}
                    style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                    <option value="">Selecione uma Localidade (Obrigatório)</option>
                    {userLocalidades && userLocalidades.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                    ))}
                </select>

                {/* Toggle para mostrar visitas canceladas */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.95em', padding: '10px' }}>
                    <input
                        type="checkbox"
                        checked={showCanceladas}
                        onChange={(e) => setShowCanceladas(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                    />
                    <span>Mostrar visitas canceladas</span>
                </label>
            </div>

            {loading ? (
                <p>Carregando dados...</p>
            ) : (
                <>
                    <p style={{ marginTop: '20px', color: '#666' }}>
                        Total de visitas encontradas: <strong>{filteredVisitas.length}</strong>
                        {visitas.length !== filteredVisitas.length && (
                            <span style={{ color: '#ff9800', marginLeft: '10px' }}>
                                (⚠️ {visitas.length - filteredVisitas.length} visitas filtradas pelos filtros de Agente/Quarteirão)
                            </span>
                        )}
                    </p>
                    <div className="table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>ID Visita</th>
                                    <th>Data</th>
                                    <th>Horário</th>
                                    <th>Agente</th>
                                    <th>Localidade</th>
                                    <th>Quarteirão</th>
                                    <th>Nº</th>
                                    <th>Comp.</th>
                                    <th>Status Visita</th>
                                    <th>Status Imóvel</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredVisitas.map(v => {
                                    const agenteKey = v.idAgente || v.emailAgente || '';
                                    const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                                    return (
                                        <tr key={v.id} style={{ backgroundColor: v.statusRegistro && v.statusRegistro !== 'ativo' ? '#ffe6e6' : 'transparent' }}>
                                            <td>
                                                <strong>{v.idVisita || '-'}</strong>
                                                {v.statusRegistro && v.statusRegistro !== 'ativo' && (
                                                    <span style={{
                                                        display: 'block',
                                                        fontSize: '0.75em',
                                                        color: '#dc3545',
                                                        fontWeight: 'bold',
                                                        marginTop: '3px'
                                                    }}>
                                                        ⚠️ {v.statusRegistro.toUpperCase()}
                                                    </span>
                                                )}
                                            </td>
                                            <td>{v.dataAtividade?.toDate?.().toLocaleDateString('pt-BR') || '—'}</td>
                                            <td>{v.dataAtividade?.toDate?.().toLocaleTimeString('pt-BR') || '—'}</td>
                                            <td>{nomeAgente}</td>
                                            <td>{v.localidade || '—'}</td>
                                            <td>{v.quarteirao || '—'}</td>
                                            <td>{v.numeroImovel || 'N/A'}</td>
                                            <td>{v.complemento || '—'}</td>
                                            <td>{v.visitaStatus || '—'}</td>
                                            <td>
                                                <span className={`status status-${v.status?.replace(' ', '-').toLowerCase()}`}>
                                                    {v.status || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <button className="view-button" onClick={() => handleOpenModal(v)}>
                                                    Visualizar
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {isModalOpen && (
                <EditModal
                    visita={selectedVisit}
                    agentsMap={agentsMap}
                    onClose={handleCloseModal}
                    onSaveSuccess={handleSaveSuccess}
                    currentUser={user}
                />
            )}
        </>
    );
}

export default HistoricoVisitasSupervisor;
